<script lang="ts" setup>
import { computed, onMounted, Ref, ref } from 'vue';
import Column from 'primevue/column';
import Chip from 'primevue/chip';
import DataTable from 'primevue/datatable';
import ArchiveSwitch from '@/components/ArchiveSwitch';
import AddPersonModal from '@/components/AddPersonModal/AddPersonModal.vue';
import { useArchiveStore, useFacilitatorStore } from '@/store';
import { FacilitatorTableInterface } from '@/types/Facilitator';
import { can } from '@/composables';
import { PERMISSIONS } from '@/enum/permissions';

onMounted(() => {
  if (can([PERMISSIONS.ORGANIZATION_FACILITATORS_INDEX])) {
    return getFacilitators(false);
  }
});

const facilitatorStore = useFacilitatorStore();
const archiveStore = useArchiveStore();
const facilitators: Ref<FacilitatorTableInterface[]> = ref([]);
const facilitatorCount: Ref<number> = computed(() => (facilitators.value ? facilitators.value.length : 0));
const facilitatorModalOpen = ref(false);
const archivedVisible = ref(false);
const facilitatorsItem: Ref<{ name: string; value: number }[]> = ref([]);

const getFacilitators: (showArchived: boolean) => Promise<void> = async (showArchived) => {
  const filterArchived = showArchived ? '?archived=1' : '';
  const facilitatorData = await facilitatorStore.getFacilitatorsForTable(filterArchived).catch((e) => {
    facilitators.value = [];
    throw e;
  });
  const filteredFacilitators = facilitatorData.filter(
    (facilitator) => facilitator.organizations && facilitator.organizations[0].id === facilitatorStore.orgId,
  );
  const parentOrgFacilitators = facilitatorData.filter(
    (facilitator) => facilitator.organizations && facilitator.organizations[0].id != facilitatorStore.orgId,
  );
  facilitators.value = [
    ...filteredFacilitators.sort((a, b) => a.name.localeCompare(b.name)),
    ...parentOrgFacilitators.sort((a, b) => a.name.localeCompare(b.name)),
  ];
};

const createMenuModel = (item: FacilitatorTableInterface) => {
  const menuItems = [
    {
      label: 'Remove Facilitator',
      visible: can([PERMISSIONS.ORGANIZATION_DETACH_FACILITATORS]),
      command: () => {
        detachFacilitators([item.id], item.name);
      },
    },
  ];
  if (item.archived) {
    menuItems.push({
      label: 'Unarchive',
      visible: can([PERMISSIONS.PEOPLE_UNARCHIVE]),
      command: async () => {
        await archiveStore.unarchive(item.id);
        archiveStore.doToast(`${item.name} has been restored`, 'Unarchived');
      },
    });
  }
  return menuItems;
};

const attachFacilitators: (ids: number[]) => Promise<void> = async (ids) => {
  await facilitatorStore.attachFacilitators({ facilitators: ids });
  await getFacilitators(false);
  facilitatorStore.doToast('Facilitator(s) has been added', 'Added');
};

const detachFacilitators: (ids: number[], facilitatorName: string) => Promise<void> = async (ids, facilitatorName) => {
  await facilitatorStore.detachFacilitators({ facilitators: ids });
  await getFacilitators(false);
  facilitatorStore.doToast(`${facilitatorName} has been removed from organization`, 'Removed');
};

const openAddFacilitators = async () => {
  facilitatorModalOpen.value = true;
  facilitatorsItem.value = await facilitatorStore.getAddFacilitatorsModalData();
};

const onCancelAdd = () => (facilitatorModalOpen.value = false);

const onSaveAdd = (facilitators: { name: string; value: number }[]) => {
  let facilitatorIds = facilitators.map((facilitator) => {
    return facilitator.value;
  });
  attachFacilitators(facilitatorIds);
  facilitatorModalOpen.value = false;
};

const showArchived = () => {
  archivedVisible.value = true;
  facilitatorStore.showArchived = true;
  getFacilitators(true);
};

const hideArchived = () => {
  archivedVisible.value = false;
  facilitatorStore.showArchived = false;
  getFacilitators(false);
};

const setRowClass = (rowData: FacilitatorTableInterface) => {
  return rowData.organizations && rowData.organizations[0].id != facilitatorStore.orgId
    ? 'inherited-service-provider-row'
    : 'service-provider-row';
};
</script>

<template>
  <Card>
    <template #header>
      <h2>Facilitators</h2>
      <Chip>{{ facilitatorCount }}</Chip>
      <ArchiveSwitch
        v-if="can([PERMISSIONS.ORGANIZATION_FACILITATORS_SHOW_ARCHIVED])"
        @enable="showArchived"
        @disable="hideArchived"
      />
      <div class="ml-auto">
        <AppButton
          v-if="can([PERMISSIONS.ORGANIZATION_ATTACH_FACILITATORS])"
          id="add-button"
          type="text"
          size="small"
          text="Add Facilitators"
          icon-left="add"
          @click="openAddFacilitators"
        />
      </div>
    </template>
    <template #content>
      <DataTable
        :value="facilitators"
        :global-filter-fields="['name']"
        :scrollable="true"
        loading-icon="logo-spinner"
        :loading="facilitatorStore.loading"
        data-key="name"
        responsive-layout="scroll"
        :row-class="setRowClass"
        scroll-height="440px"
        state-key="session-store-facilitators-table"
        state-storage="session"
        :class="{ 'table-loading': facilitatorStore.loading }"
      >
        <template v-if="facilitatorCount">
          <Column
            body-style="max-width: 20rem"
            field="name"
            header="Facilitator Name"
            header-style="max-width: 20rem; color: #3B4242;"
            sortable
          >
            <template #body="{ data }">
              <Icon
                v-if="data.archived"
                v-tooltip.top="{
                  value: 'Archived',
                  escape: true,
                  fitContent: true,
                }"
                name="info"
                class="mr-1 text-color-gray-dark text-base cursor-pointer"
              />
              <RouterLink
                v-if="can([PERMISSIONS.PEOPLE_SHOW])"
                :to="`/user/${data.id}`"
                class="font-semibold"
                v-text="data.name"
              />
              <span v-else class="font-semibold" v-text="data.name" />
            </template>
          </Column>
          <Column field="organizations" header="Organization">
            <template #body="{ data }">
              <RouterLink
                v-if="data.organizations[0].id != facilitatorStore.orgId"
                class="text-color-secondary underline"
                :to="`/organizations/${data.organizations[0].id}`"
                v-text="data.organizations[0].name"
              />
              <span v-else v-text="data.organizations[0].name" />
            </template>
          </Column>
          <Column
            v-if="can([PERMISSIONS.ORGANIZATION_DETACH_FACILITATORS, PERMISSIONS.PEOPLE_UNARCHIVE])"
            body-style="justify-content: center; font-size: .8725rem; max-width: 10rem;"
            header-style="justify-content: center; max-width: 10rem"
            field="id"
            header="Actions"
          >
            <template #body="{ data }">
              <ActionMenu
                v-if="data.organizations[0].id == facilitatorStore.orgId"
                :menu-items="createMenuModel(data)"
                icon="more_horiz"
              />
            </template>
          </Column>
        </template>
        <template #empty>Facilitators not assigned</template>
      </DataTable>
      <AddPersonModal
        v-if="facilitatorModalOpen && can([PERMISSIONS.ORGANIZATION_ATTACH_FACILITATORS])"
        title="Add facilitators"
        :items="facilitatorsItem"
        :searchable="true"
        list-header-text="Facilitator name"
        button-text="Add facilitators"
        placeholder="Search facilitators"
        person-type="facilitator"
        @add-items="onSaveAdd"
        @cancel="onCancelAdd"
      />
    </template>
  </Card>
</template>

<style lang="scss" scoped>
.p-card {
  min-width: calc(100% - 1.5rem);
  .p-card-body {
    padding-bottom: 0px;
  }
}

:deep(.inherited-service-provider-row) {
  td {
    background: var(--surface-50);
  }
}
</style>
